const {Component} = require("react");
const PropTypes = require("prop-types");
const router = require("services/router");
const {classList} = require("helpers/html");
const moment = require("moment");

class Booking extends Component {

    static get propTypes() {
        return {
            id: PropTypes.number.isRequired,
            from: PropTypes.string.isRequired,
            to: PropTypes.string.isRequired,
            order: PropTypes.object.isRequired,
            booker: PropTypes.object,
            bookable: PropTypes.object,
            created_at: PropTypes.string.isRequired,
            accepted_at: PropTypes.any,
            status: PropTypes.string.isRequired,
            requests: PropTypes.array,
            viewAs: PropTypes.string,
            handleAccept: PropTypes.func,
            handleCancel: PropTypes.func,
            handleChange: PropTypes.func,
            handleInfo: PropTypes.func,
            handleAcceptRequest: PropTypes.func,
            handleRejectRequest: PropTypes.func,
            defaultAvatarUrl: PropTypes.string
        };
    }

    static get defaultProps() {
        return {
            viewAs: "bookable",
            defaultAvatarUrl: "img/avatar-placeholder.png"
        };
    }

    constructor(props) {
        super(props);

        this.state = {
            loading: false
        };
    }

    get person() {
        return this.props.viewAs === "bookable" ? this.props.bookable : this.props.booker;
    }

    get otherPerson() {
        return this.props.viewAs === "bookable" ? this.props.booker : this.props.bookable;
    }

    get activeChangeRequest() {
        return this.props.requests.filter(request => request.status === "pending") [0];
    }

    get suggestedChangeRequest() {
        const person = this.person;
        return this.props.requests.filter(request => (request.status === "pending") && (request.requester_id === person.id)) [0];
    }

    get receivedChangeRequest() {
        const person = this.otherPerson;
        return this.props.requests.filter(request => (request.status === "pending") && (request.requester_id === person.id)) [0];
    }

    prettyRoleName(role) {
        switch (role) {
            case "student":
                return "Elev";

            case "teacher":
                return "Underviser";

            case "admin":
                return "Administrator";

            default:
                return role;
        }
    }

    get prettyStatus() {
        if (this.activeChangeRequest) {
            return "Afventer godkendelse af tidspunkt";
        }

        switch (this.props.status) {
            case "booked":
                return "Afventer godkendelse";

            case "accepted":
                return "Aktiv booking";

            case "completed":
                return "Færdig booking";

            case "cancelled":
                if (this.props.accepted_at === null) {
                    return "Afvist booking";
                } else {
                    return "Annulleret booking";
                }
        }
    }

    get className() {
        return classList(
            "booking-item",
            this.props.status === "booked" ? "approval" : null,
            this.props.status === "accepted" ? "active" : null,
            this.props.status === "completed" ? "complete" : null,
            this.props.status === "cancelled" ? "inactive" : null,
            this.activeChangeRequest && "awaiting-time-approval",
            this.state.loading ? "loading" : null,
            this.props.viewAs === "bookable" ? "as-teacher" : "as-student",
            this.props.requesting && this.props.viewAs === "bookable" ? "flagged" : null
        );
    }

    /////////////////////////////////
    // Helpers
    /////////////////////////////////

    setLoadingState(loading = true) {
        this.setState({loading});
    }

    handleAccept = (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.setLoadingState(true);
        const response = typeof this.props.handleAccept === "function" ?
            this.props.handleAccept(this.props.id) : null;

        if (response && typeof response.then === "function") {
            return response
                .then(() => this.setLoadingState(false))
                .catch(() => this.setLoadingState(false));
        }

        this.setLoadingState(false);
    };

    handleCancel = (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.setLoadingState(true);
        const response = typeof this.props.handleCancel === "function" ?
            this.props.handleCancel(this.props.id) : null;

        if (response && typeof response.then === "function") {
            return response
                .then(() => this.setLoadingState(false))
                .catch(() => this.setLoadingState(false));
        }

        this.setLoadingState(false);
    };

    handleInfo = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (typeof this.props.handleInfo === "function") {
            this.props.handleInfo(this.props.id);
        }
    }

    handleChange = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (typeof this.props.handleChange === "function") {
            this.props.handleChange(this.props.id);
        }
    };

    handleAcceptRequest = (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.setLoadingState(true);
        const response = typeof this.props.handleAcceptRequest === "function" ?
            this.props.handleAcceptRequest(this.props.id) : null;

        if (response && typeof response.then === "function") {
            return response
                .then(() => this.setLoadingState(false))
                .catch(() => this.setLoadingState(false));
        }

        this.setLoadingState(false);
    };

    handleRejectRequest = (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.setLoadingState(true);
        const response = typeof this.props.handleRejectRequest === "function" ?
            this.props.handleRejectRequest(this.props.id) : null;

        if (response && typeof response.then === "function") {
            return response
                .then(() => this.setLoadingState(false))
                .catch(() => this.setLoadingState(false));
        }

        this.setLoadingState(false);
    };

    /////////////////////////////////
    // Recipient / Bookable
    /////////////////////////////////

    renderRecipients() {
        const booker = this.props.booker;
        let recipients = [].concat(this.props.order.subuserRecipients);

        if (this.props.order.userRecipient) {
            recipients.unshift(this.props.order.userRecipient);
        }

        // Prepare markups
        const avatarMarkup = recipients.map(recipient => {
            const url = recipient.avatar ? recipient.avatar.url : router.url(this.props.defaultAvatarUrl);
            return (<img key={recipient.id} src={url} alt={recipient.name}/>);
        });

        return (
            <div className="student-box">

                <div className="avatars">
                    {avatarMarkup}
                </div>

                <div className="name-box">
                    <p className="name">{booker.name}</p>
                    <p>{this.prettyRoleName(booker.primary_role)}</p>
                </div>
            </div>
        );
    }

    renderBookable() {
        const bookable = this.props.bookable;
        const avatarUrl = bookable.avatar ? bookable.avatar.url : router.url(this.props.defaultAvatarUrl);
        const link = router.route("profile", {username: bookable.username});

        return (
            <div className="student-box">
                <a href={link} ><img src={avatarUrl} alt={bookable.name}/></a>

                <div className="name-box">
                    <a href={link} ><p className="name">{bookable.name}</p></a>
                    <p>{this.prettyRoleName(bookable.primary_role)}</p>
                </div>
            </div>
        );
    }

    renderFlags() {
        if (!this.props.requesting || this.props.viewAs === "booker") return null;

        return (
            <div className="flag danger">
                <p>Vær opmærksom på at denne booking er uden for din kalendar</p>
            </div>
        );
    }

    renderPeople() {
        return this.props.viewAs === "bookable" ?
            this.renderRecipients() :
            this.renderBookable();
    }

    /////////////////////////////////
    // State
    /////////////////////////////////

    renderStatus() {
        return (
            <div className="state">
                <p>{this.prettyStatus}</p>
            </div>
        );
    }

    /////////////////////////////////
    // Time
    /////////////////////////////////

    renderTime() {
        const from = moment(this.props.from);
        const to = moment(this.props.to);
        const changeRequest = this.activeChangeRequest;
        let requestMarkup = null;

        if (changeRequest) {
            const requestFrom = moment(changeRequest.mutation.from);
            const requestTo = moment(changeRequest.mutation.to);

            requestMarkup = (
                <div className="change-time">
                    <p>{requestFrom.format("DD[.] MMMM")} / {requestFrom.format("HH:mm")} - {requestTo.format("HH:mm")}</p>
                </div>
            );
        }

        return (
            <div className="time">
                <div className="cur-time">
                    <span>Aftalt tidspunkt</span>
                    <p>{from.format("DD[.] MMMM")} / {from.format("HH:mm")} - {to.format("HH:mm")}</p>
                </div>

                {requestMarkup}

                <div className="new-time" onClick={this.handleChange}>
                    <p>{from.format("DD[.] MMMM")} / {from.format("HH:mm")} - {to.format("HH:mm")}</p>
                </div>

            </div>
        );
    }

    /////////////////////////////////
    // Created
    /////////////////////////////////

    renderCreation() {
        const createdAt = moment(this.props.created_at);

        return (
            <div className="created">
                <span>Oprettet</span>
                <p>{createdAt.format("YYYY/MM/DD")}</p>
            </div>
        );
    }

    /////////////////////////////////
    // Quick launch
    /////////////////////////////////

    renderQuickLaunchItem() {
        if (this.suggestedChangeRequest) {
            return (<p>Afventer</p>);
        }

        if (this.receivedChangeRequest) {
            return (
                <div className="change-request-actions">
                    <button type="button" className="accept-request" onClick={this.handleAcceptRequest}>
                        Godkend nyt tidspunkt
                    </button>
                    <button type="button" className="reject-request" onClick={this.handleRejectRequest}>
                        Afvis nyt tidspunkt
                    </button>
                </div>
            );
        }

        switch (this.props.status) {
            case "booked":
                return this.props.viewAs === "bookable" ?
                    (<button type="button" onClick={this.handleAccept}>Godkend</button>) :
                    (<p>Afventer</p>);

            case "accepted":
                return (<p>Godkendt</p>);

            case "cancelled":
                return (<p>Annulleret</p>);

            case "completed":
                return (<p>Færdig</p>);
        }
    }

    renderQuickLaunch() {
        return (
            <div className="status">{this.renderQuickLaunchItem()}</div>
        );
    }

    /////////////////////////////////
    // Options
    /////////////////////////////////

    renderOptions() {

        const cancelAction = this.props.viewAs === 'bookable' && this.props.status === 'booked' ?
            'Afvis' :
            'Aflys';

        let options = [];
        const key = () => options.length;

		if (this.props.accepted_at !== null) {
			options.push(
				<li key={key()}>
					<span>Faktura</span>
					<a href={router.route('backend.invoices.stream', {booking_id: this.props.id})} target="_blank">Se faktura</a>
					<a href={router.route('backend.invoices.download', {booking_id: this.props.id})} target="_blank">Download faktura</a>
				</li>
			);
		}

        if (!((this.props.status === "completed") || (this.props.status === "cancelled"))) {
            options.push(
                <li key={key()}>
                    <span>Rediger</span>
                    <button type="button" onClick={this.handleChange} disabled={this.activeChangeRequest ? true : false}>
                        Forespørg om nyt tidspunkt
                    </button>
                </li>
            );
            options.push(

                <li key={key()}>
                    <span>Annuller booking</span>
                    <button type="button" onClick={this.handleCancel}>{cancelAction} bookingen</button>
                </li>
            );
        }

        if (options.length === 0) {
            return;
        }

        return (
            <div className="options">
                <div className="option-dot"></div>
                <div className="option-dot"></div>
                <div className="option-dot"></div>

                <div className="options-box">
                    <div className="hover-filler"></div>

                    <ul>
                        {options}
                    </ul>

                </div>
            </div>
        );
    }

    renderInfo() {
        if (this.props.viewAs !== "bookable") return;

        return (
            <div className="notes" onClick={this.handleInfo}>
                <img src={router.url("img/icons/info.svg")} alt="Info"/>
            </div>
        );
    }

    render() {
        return (
            <div className={this.className}>
                {this.renderFlags()}
                {this.renderPeople()}
                {this.renderInfo()}
                {this.renderStatus()}
                {this.renderTime()}
                {this.renderCreation()}
                {this.renderQuickLaunch()}
                {this.renderOptions()}
                <div className="loader">
                    <svg className="circle-loader" width="40" height="40" version="1.1" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="20" r="8"/>
                    </svg>
                </div>
            </div>
        );
    }

}

module.exports = Booking;



// WEBPACK FOOTER //
// ./resources/js/components/backend/bookings/Booking.js